import React, { useState } from 'react';
import {
  Alert,
  Card,
  Image,
  message,
  Space,
  Switch,
  Tooltip,
  Button,
} from 'antd';
import { useTranslation } from '@refinedev/core';
import { getFileUrl } from '@/Helpers';
import { ListButton, RefreshButton, Show } from '@refinedev/antd';
import { endPoints } from '@/api/configs';
import useApi, { useGet } from '@/api/useApi';
import { useParams } from 'react-router-dom';
import { PostReq } from '@/api/types/getOnePost';
import { SectionCard } from '@/components/sections/SectionCard';
import { PostInfoCard } from '@/components/posts/PostInfoCard';
import { EditSectionModal } from '@/components/modals/EditSectionModal';
import { EditOutlined } from '@ant-design/icons';
import { EditPostModal } from '@/components/modals/EditPostModal';

export const ShowPost: React.FC = () => {
  const { id } = useParams();
  const { usePost } = useApi();
  const [bannerError, setBannerError] = useState(false);
  const [editSectionModalVisible, setEditSectionModalVisible] = useState(false);
  const [selectedSection, setSelectedSection] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { translate } = useTranslation();
  const { mutate: toggleFeaturedPost, isLoading: isToggleFeaturedLoading } =
    usePost(endPoints.toggleFeaturedPost(id || ''));
  const { data, isLoading, refetch } = useGet<PostReq>(
    endPoints.post(id),
    endPoints.post(id)
  );
  const onToggleFeaturedPost = () => {
    toggleFeaturedPost(
      {},
      {
        onSuccess: () => {
          message.success(
            post?.is_featured
              ? translate('posts.removed-from-featured')
              : translate('posts.added-to-featured')
          );
          refetch();
        },
        onError: () => {
          message.error(translate('posts.error-adding-to-featured'));
        },
      }
    );
  };
  const handleEditSection = (section: any) => {
    setSelectedSection(section);
    setEditSectionModalVisible(true);
  };
  const post = data?.data?.data;

  return (
    <Show
      isLoading={isLoading}
      title={translate('listing-posts.show-post')}
      headerButtons={({ listButtonProps }) => (
        <>
          {listButtonProps && (
            <Space>
              <Tooltip
                title={
                  post?.is_featured
                    ? translate('posts.remove-featured')
                    : translate('posts.is-featured')
                }
              >
                <Switch
                  checked={post?.is_featured}
                  onChange={onToggleFeaturedPost}
                  disabled={isToggleFeaturedLoading}
                  loading={isToggleFeaturedLoading}
                />
              </Tooltip>
              <ListButton {...listButtonProps}>
                {translate('buttons.list')}
              </ListButton>
              {!post?.is_published && (
                <Button
                  onClick={() => setIsEditModalOpen(true)}
                  icon={<EditOutlined />}
                  type="primary"
                >
                  {translate('buttons.edit')}
                </Button>
              )}
              <RefreshButton />
            </Space>
          )}
        </>
      )}
    >
      <PostInfoCard
        postReq={post}
        onAddComment={() => {}}
        isLoading={isLoading}
        onChangeRequest={() => {}}
      />

      <Card className="mb-6">
        {post?.banner &&
          (!bannerError ? (
            <div>
              <Image
                src={getFileUrl(post.banner.id.toString())}
                alt={post.banner.file_name}
                className="w-full object-cover rounded-lg"
                preview={{
                  mask: <div className="text-lg">Click to view</div>,
                }}
                onError={() => {
                  setBannerError(true);
                }}
              />
              {post.banner.description && (
                <div className="text-sm text-gray-500">
                  {post.banner.description}
                </div>
              )}
            </div>
          ) : (
            <Alert message={translate('messages.missing-media')} type="error" />
          ))}
      </Card>
      {post?.sections?.map((section) => (
        <SectionCard
          key={section.id}
          refetch={refetch}
          reqStatus={post.is_published ? 'APPROVED' : 'PENDING'}
          section={section}
          onAddComment={() => {}}
          onChangeRequest={() => {}}
          onEdit={handleEditSection}
        />
      ))}
      {editSectionModalVisible && selectedSection && (
        <EditSectionModal
          open={editSectionModalVisible}
          onCancel={() => {
            setEditSectionModalVisible(false);
            setSelectedSection(null);
          }}
          refetch={refetch}
          postId={id || ''}
          sectionId={selectedSection.id.toString()}
          initialValues={{
            type: selectedSection.type,
            view: selectedSection.view,
            title: selectedSection.title,
            content: selectedSection.content,
            quote: selectedSection.quote,
            description: selectedSection.description,
            uploads: selectedSection.uploads,
          }}
        />
      )}
      {post && (
        <EditPostModal
          open={isEditModalOpen}
          onCancel={() => setIsEditModalOpen(false)}
          refetch={refetch}
          postId={id || ''}
          initialValues={{
            title: post.title || '',
            view_period: post.view_period || '',
            location: post.location || '',
            type: post.type || '',
            date: post.date || '',
            summary: post.summary || '',
            topics: post.topics?.map((topic) => topic.id) || [],
            banner_id: post.banner_id || post.banner?.id,
            main_audio_id: post.main_audio_id || post.main_audio?.id || 0,
            main_video_id: post.main_video_id || post.main_video?.id || 0,
          }}
        />
      )}
    </Show>
  );
};
